import { AIConfig, AIAspirationSuggestion, AIAspirationRequest, AIGoalSuggestion, AIGoalRequest, AIListSuggestion, AIListRequest, AIItemSuggestion, AIItemRequest, AIScheduleSuggestion, AIScheduleRequest, AIScheduleOptimizationRequest, AIScheduleOptimizationResult, AIDebugEntry, AIAspirationReport, AIReportRequest } from '../types/ai';
import { environment } from '../config/environment';

const AI_CONFIG_KEY = 'godmode_ai_config';
const AI_DEBUG_KEY = 'godmode_ai_debug';
const AI_USER_KEY = 'godmode_user_anthropic_key';

class AIService {
  private config: AIConfig | null = null;
  private debugHistory: AIDebugEntry[] = [];
  private maxDebugEntries = 50; // Keep last 50 entries
  private userApiKey: string | null = null;

  constructor() {
    this.loadConfig();
    this.loadDebugHistory();
    this.loadUserApiKey();
  }

  private loadConfig(): void {
    const stored = localStorage.getItem(AI_CONFIG_KEY);
    if (stored) {
      this.config = JSON.parse(stored);
    }
  }

  private loadDebugHistory(): void {
    try {
      const stored = localStorage.getItem(AI_DEBUG_KEY);
      if (stored) {
        this.debugHistory = JSON.parse(stored).map((entry: any) => ({
          ...entry,
          timestamp: new Date(entry.timestamp)
        }));
      }
    } catch (error) {
      console.error('Failed to load AI debug history:', error);
      this.debugHistory = [];
    }
  }

  private loadUserApiKey(): void {
    const stored = localStorage.getItem(AI_USER_KEY);
    if (stored) {
      try {
        // Decrypt if using encryption service (future enhancement)
        this.userApiKey = stored;
      } catch (error) {
        console.error('Failed to load user API key:', error);
      }
    }
  }

  private saveDebugEntry(entry: AIDebugEntry): void {
    this.debugHistory.unshift(entry); // Add to beginning
    
    // Keep only last N entries
    if (this.debugHistory.length > this.maxDebugEntries) {
      this.debugHistory = this.debugHistory.slice(0, this.maxDebugEntries);
    }
    
    // Save to localStorage
    try {
      localStorage.setItem(AI_DEBUG_KEY, JSON.stringify(this.debugHistory));
      console.log(`🔍 AI Debug Entry saved: ${entry.id}`);
      console.log('Access debug data with: JSON.parse(localStorage.getItem("godmode_ai_debug"))');
    } catch (error) {
      console.error('Failed to save AI debug entry:', error);
    }
  }

  saveConfig(config: Partial<AIConfig>): void {
    // Save UI preferences (API key is stored separately)
    const uiConfig: AIConfig = {
      apiKey: '', // API key is stored separately for security
      provider: 'anthropic', // Only use Anthropic now
      model: config.model || '',
      enabled: config.enabled ?? true
    };

    this.config = uiConfig;
    localStorage.setItem(AI_CONFIG_KEY, JSON.stringify(uiConfig));
    console.log('💾 AI UI config saved:', {
      provider: uiConfig.provider,
      model: uiConfig.model,
      enabled: uiConfig.enabled
    });
  }

  getConfig(): AIConfig | null {
    return this.config;
  }

  isConfigured(): boolean {
    // Now check if user has provided their own API key and AI is enabled
    return this.hasUserApiKey() && !!(this.config?.enabled);
  }

  saveUserApiKey(apiKey: string): void {
    if (!apiKey) {
      localStorage.removeItem(AI_USER_KEY);
      this.userApiKey = null;
      return;
    }

    // Basic validation
    if (!apiKey.startsWith('sk-ant-')) {
      throw new Error('Invalid Anthropic API key format');
    }

    // Store encrypted if possible (future enhancement)
    localStorage.setItem(AI_USER_KEY, apiKey);
    this.userApiKey = apiKey;
  }

  getUserApiKey(): string | null {
    return this.userApiKey;
  }

  hasUserApiKey(): boolean {
    return !!this.userApiKey;
  }

  getActiveApiKey(): string | null {
    return this.userApiKey;
  }

  async fetchAnthropicModels(): Promise<{ id: string; display_name: string }[]> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.userApiKey) {
      headers['x-user-api-key'] = this.userApiKey;
    }

    try {
      const response = await fetch('/api/ai/anthropic-models', { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.status}`);
      }

      const data = await response.json();
      return data.models;
    } catch (error) {
      console.error('Failed to fetch models from API, using fallback:', error);
      // Fallback list
      return [
        { id: 'claude-3-5-sonnet-20241022', display_name: 'Claude 3.5 Sonnet (Latest)' },
        { id: 'claude-3-opus-20240229', display_name: 'Claude 3 Opus' },
        { id: 'claude-3-haiku-20240307', display_name: 'Claude 3 Haiku (Fast)' }
      ];
    }
  }

  getEnvironmentStatus() {
    return environment.getConfigurationStatus();
  }

  private async makeAIRequest(endpoint: string, body: any): Promise<any> {
    // User key is required
    if (!this.userApiKey) {
      throw new Error('API key required. Please add your Anthropic API key in Settings.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-user-api-key': this.userApiKey
    };

    const response = await fetch(`${environment.aiEndpoint}/${endpoint}`, {
      method: 'POST',
      headers,
      credentials: environment.isProduction ? 'same-origin' : 'omit',
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid or missing API key. Please add your Anthropic API key in Settings.');
      }
      const errorData = await response.json();
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    return response.json();
  }

  // Public method to get debug history
  getDebugHistory(): AIDebugEntry[] {
    return this.debugHistory;
  }

  // Public method to clear debug history
  clearDebugHistory(): void {
    this.debugHistory = [];
    localStorage.removeItem(AI_DEBUG_KEY);
    console.log('🧹 AI debug history cleared');
  }

  // Export debug data for sharing
  exportDebugData(): string {
    const debugData = {
      config: {
        provider: this.config?.provider,
        model: this.config?.model,
        enabled: this.config?.enabled,
        hasEnvironmentConfig: environment.isConfigured()
      },
      environment: environment.getConfigurationStatus(),
      history: this.debugHistory,
      timestamp: new Date().toISOString()
    };
    
    return JSON.stringify(debugData, null, 2);
  }

  private buildUserPrompt(request: AIAspirationRequest): string {
    let prompt = `User Request: "${request.userInput}"\n\n`;
    
    if (request.existingAspirations.length > 0) {
      prompt += `Existing Aspirations (avoid duplicates):\n`;
      prompt += request.existingAspirations.map(a => `- ${a.title} (${a.category}): ${a.description}`).join('\n');
      prompt += '\n\n';
    }
    
    if (request.currentFormData && Object.values(request.currentFormData).some(v => v)) {
      prompt += `Current Form Data (please enhance/refine rather than replace):\n`;
      if (request.currentFormData.title) prompt += `Title: "${request.currentFormData.title}"\n`;
      if (request.currentFormData.description) prompt += `Description: "${request.currentFormData.description}"\n`;
      if (request.currentFormData.category) prompt += `Category: "${request.currentFormData.category}"\n`;
      if (request.currentFormData.deadline) prompt += `Deadline: "${request.currentFormData.deadline}"\n`;
      prompt += '\n';
    }
    
    if (request.userProfile) {
      prompt += `User Context:\n`;
      if (request.userProfile.name) prompt += `Name: ${request.userProfile.name}\n`;
      if (request.userProfile.jobRole) prompt += `Role: ${request.userProfile.jobRole}\n`;
      if (request.userProfile.age) prompt += `Age: ${request.userProfile.age}\n`;
      prompt += '\n';
    }
    
    return prompt;
  }

  private buildGoalUserPrompt(request: AIGoalRequest): string {
    let prompt = `User Request: "${request.userInput}"\n\n`;
    
    prompt += `Linked Aspiration:\n`;
    prompt += `- ${request.linkedAspiration.title} (${request.linkedAspiration.category}): ${request.linkedAspiration.description}\n\n`;
    
    if (request.existingGoals.length > 0) {
      prompt += `Existing Goals for this Aspiration (avoid duplicates):\n`;
      prompt += request.existingGoals
        .filter(g => g.aspirationId === request.linkedAspiration.id)
        .map(g => `- ${g.title}: ${g.description}`)
        .join('\n');
      prompt += '\n\n';
    }
    
    if (request.currentFormData && Object.values(request.currentFormData).some(v => v)) {
      prompt += `Current Form Data (please enhance/refine rather than replace):\n`;
      if (request.currentFormData.title) prompt += `Title: "${request.currentFormData.title}"\n`;
      if (request.currentFormData.description) prompt += `Description: "${request.currentFormData.description}"\n`;
      if (request.currentFormData.smartCriteria) {
        const sc = request.currentFormData.smartCriteria;
        if (sc.specific) prompt += `Specific: "${sc.specific}"\n`;
        if (sc.measurable) prompt += `Measurable: "${sc.measurable}"\n`;
        if (sc.achievable) prompt += `Achievable: "${sc.achievable}"\n`;
        if (sc.relevant) prompt += `Relevant: "${sc.relevant}"\n`;
        if (sc.timeBound) prompt += `Time-bound: "${sc.timeBound}"\n`;
      }
      if (request.currentFormData.successMetrics?.length) {
        prompt += `Success Metrics: ${request.currentFormData.successMetrics.join(', ')}\n`;
      }
      if (request.currentFormData.resources?.length) {
        prompt += `Resources: ${request.currentFormData.resources.join(', ')}\n`;
      }
      prompt += '\n';
    }
    
    if (request.userProfile) {
      prompt += `User Context:\n`;
      if (request.userProfile.name) prompt += `Name: ${request.userProfile.name}\n`;
      if (request.userProfile.jobRole) prompt += `Role: ${request.userProfile.jobRole}\n`;
      if (request.userProfile.age) prompt += `Age: ${request.userProfile.age}\n`;
      prompt += '\n';
    }
    
    return prompt;
  }

  async generateGoalSuggestion(request: AIGoalRequest): Promise<AIGoalSuggestion> {
    // Check if user has provided their API key
    if (!this.hasUserApiKey()) {
      throw new Error('API key required. Please add your Anthropic API key in Settings.');
    }

    // Check UI configuration
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled. Please enable them in Settings.');
    }

    // Only use Anthropic now
    const provider = 'anthropic';

    const systemPrompt = `You are a goal-setting coach AI helping users create SMART goals that align with their aspirations. 
    Based on the user's input (or lack thereof), the linked aspiration, existing goals, and any current form data, suggest a goal that:
    1. Is specific, measurable, achievable, relevant, and time-bound (SMART)
    2. Directly advances the linked aspiration
    3. Complements but doesn't duplicate existing goals
    4. If current form data exists, enhance/refine it rather than completely replacing it
    5. If the user input is generic like "Suggest a SMART goal", analyze the aspiration and create a meaningful goal that would advance it
    6. Provides clear, actionable SMART criteria
    7. Suggests concrete metrics and resources
    
    Return a JSON object with: 
    - title (max 100 chars)
    - description (max 500 chars)
    - smartCriteria object with all 5 SMART fields (each at least 10 chars): specific, measurable, achievable, relevant, timeBound
    - suggestedMetrics array (optional, 3-5 concrete metrics)
    - suggestedResources array (optional, 3-5 specific resources)
    - estimatedDuration (optional, e.g., "3 months")
    - reasoning (optional, brief explanation)`;

    const userPrompt = this.buildGoalUserPrompt(request);

    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: request.userInput,
        existingAspirations: 0, // Not used for goals
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt,
        currentFormData: request.currentFormData
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      // Generate request ID for tracking
      const requestId = crypto.randomUUID();
      
      console.log(`🎯 Sending AI Goal request ${requestId} to backend...`);

      // Call backend with user API key
      const { data: suggestion, meta } = await this.makeAIRequest('goal', {
        userInput: request.userInput,
        linkedAspiration: request.linkedAspiration,
        existingGoals: request.existingGoals,
        userProfile: request.userProfile,
        currentFormData: request.currentFormData,
        provider,
        model: this.config?.model,
        requestId
      });
      const duration = Date.now() - startTime;

      // Update debug entry with success
      debugEntry.response = {
        success: true,
        data: { suggestion, meta },
        duration
      };
      debugEntry.parsedResult = suggestion;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI goal suggestion generated in ${meta.duration}ms (request: ${meta.requestId})`);

      return suggestion;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Update debug entry with error
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);

      console.error('AI goal generation failed:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else if (error.message.includes('Invalid authorization')) {
          throw new Error('Authentication failed. Please check your deployment configuration.');
        } else if (error.message.includes('not configured')) {
          throw new Error('AI provider not configured on server. Please check environment variables.');
        } else {
          throw new Error(`Failed to generate goal suggestion: ${error.message}`);
        }
      } else {
        throw new Error('Failed to generate goal suggestion. Please try again.');
      }
    }
  }

  async generateListSuggestion(request: AIListRequest): Promise<AIListSuggestion> {
    // Check if user has provided their API key
    if (!this.hasUserApiKey()) {
      throw new Error('API key required. Please add your Anthropic API key in Settings.');
    }

    // Check UI configuration
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled. Please enable them in Settings.');
    }

    // Only use Anthropic now
    const provider = 'anthropic';

    const systemPrompt = `You are a productivity coach AI helping users create actionable lists that support their SMART goals. 
    Based on the user's input (or lack thereof), the linked goal, existing lists, and any current form data, suggest a list that:
    1. Directly supports achieving the linked SMART goal
    2. Serves as a container for future actionable tasks
    3. Complements but doesn't duplicate existing lists for the same goal
    4. If current form data exists, enhance/refine it rather than completely replacing it
    5. If the user input is generic like "help me organize this goal", analyze the goal and create a meaningful list structure
    6. Focuses on the list's purpose and organization rather than specific tasks
    
    Return a JSON object with: 
    - title (max 100 chars, specific to the goal)
    - description (max 500 chars, explains how this list will help achieve the goal)
    - estimatedHours (optional, realistic time estimate for completing this list)
    - priority (optional, 1-10 based on goal urgency)
    - color (optional, hex color for organization)
    - reasoning (optional, brief explanation of the approach)`;

    const userPrompt = this.buildListUserPrompt(request);

    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_list_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: request.userInput,
        existingAspirations: 0, // Not used for lists
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt,
        currentFormData: request.currentFormData
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      // Generate request ID for tracking
      const requestId = crypto.randomUUID();
      
      console.log(`📋 Sending AI List request ${requestId} to backend...`);

      // Call backend with user API key
      const { data: suggestion, meta } = await this.makeAIRequest('list', {
        userInput: request.userInput,
        linkedGoal: request.linkedGoal,
        existingLists: request.existingLists,
        userProfile: request.userProfile,
        currentFormData: request.currentFormData,
        provider,
        model: this.config?.model,
        requestId
      });
      const duration = Date.now() - startTime;

      // Update debug entry with success
      debugEntry.response = {
        success: true,
        data: { suggestion, meta },
        duration
      };
      debugEntry.parsedResult = suggestion;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI list suggestion generated in ${meta.duration}ms (request: ${meta.requestId})`);

      return suggestion;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Update debug entry with error
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);

      console.error('AI list generation failed:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else if (error.message.includes('Invalid authorization')) {
          throw new Error('Authentication failed. Please check your deployment configuration.');
        } else if (error.message.includes('not configured')) {
          throw new Error('AI provider not configured on server. Please check environment variables.');
        } else {
          throw new Error(`Failed to generate list suggestion: ${error.message}`);
        }
      } else {
        throw new Error('Failed to generate list suggestion. Please try again.');
      }
    }
  }

  private buildListUserPrompt(request: AIListRequest): string {
    let prompt = `User Request: "${request.userInput}"\n\n`;
    
    prompt += `Linked Goal (SMART Framework):\n`;
    prompt += `- Title: ${request.linkedGoal.title}\n`;
    prompt += `- Description: ${request.linkedGoal.description}\n`;
    prompt += `- Specific: ${request.linkedGoal.smartCriteria.specific}\n`;
    prompt += `- Measurable: ${request.linkedGoal.smartCriteria.measurable}\n`;
    prompt += `- Achievable: ${request.linkedGoal.smartCriteria.achievable}\n`;
    prompt += `- Relevant: ${request.linkedGoal.smartCriteria.relevant}\n`;
    prompt += `- Time-bound: ${request.linkedGoal.smartCriteria.timeBound}\n\n`;
    
    if (request.existingLists.length > 0) {
      prompt += `Existing Lists for this Goal (avoid duplicates):\n`;
      prompt += request.existingLists
        .filter(l => l.goalId === request.linkedGoal.id)
        .map(l => `- ${l.title} (${l.status}): ${l.description}`)
        .join('\n');
      prompt += '\n\n';
    }
    
    if (request.currentFormData && Object.values(request.currentFormData).some(v => v)) {
      prompt += `Current Form Data (please enhance/refine rather than replace):\n`;
      if (request.currentFormData.title) prompt += `Title: "${request.currentFormData.title}"\n`;
      if (request.currentFormData.description) prompt += `Description: "${request.currentFormData.description}"\n`;
      if (request.currentFormData.status) prompt += `Status: "${request.currentFormData.status}"\n`;
      if (request.currentFormData.color) prompt += `Color: "${request.currentFormData.color}"\n`;
      prompt += '\n';
    }
    
    if (request.userProfile) {
      prompt += `User Context:\n`;
      if (request.userProfile.name) prompt += `Name: ${request.userProfile.name}\n`;
      if (request.userProfile.jobRole) prompt += `Role: ${request.userProfile.jobRole}\n`;
      if (request.userProfile.age) prompt += `Age: ${request.userProfile.age}\n`;
      prompt += '\n';
    }
    
    return prompt;
  }

  async generateItemSuggestions(request: AIItemRequest): Promise<AIItemSuggestion> {
    // Check if user has provided their API key
    if (!this.hasUserApiKey()) {
      throw new Error('API key required. Please add your Anthropic API key in Settings.');
    }

    // Check UI configuration
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled. Please enable them in Settings.');
    }

    // Only use Anthropic now
    const provider = 'anthropic';

    const systemPrompt = `You are a productivity expert AI helping users break down their lists into specific, actionable items.
    Based on the user's input, the list context, linked goal, and existing items, suggest 3-7 new actionable items that:
    1. Are specific, concrete, and immediately actionable (not vague or abstract)
    2. Support the list's purpose and advance the linked SMART goal
    3. Complement but don't duplicate existing items
    4. Are appropriately sized - small enough to complete in one session but meaningful
    5. Include realistic time estimates and difficulty levels
    6. Are ordered by logical priority/sequence
    
    Return a JSON object with:
    - items array (3-7 items, each with: content, description?, estimatedMinutes?, difficulty?, category?, priority)
    - reasoning (optional, brief explanation of approach)
    
    Keep each item's content under 100 characters. Make them actionable with clear verbs.`;

    const userPrompt = this.buildItemUserPrompt(request);

    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_items_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: request.userInput,
        existingAspirations: 0, // Not used for items
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      // Generate request ID for tracking
      const requestId = crypto.randomUUID();
      
      console.log(`📝 Sending AI Items request ${requestId} to backend...`);

      // Call backend with user API key
      const { data: suggestion, meta } = await this.makeAIRequest('items', {
        userInput: request.userInput,
        linkedList: request.linkedList,
        linkedGoal: request.linkedGoal,
        existingItems: request.existingItems,
        userProfile: request.userProfile,
        provider,
        model: this.config?.model,
        requestId
      });
      const duration = Date.now() - startTime;

      // Update debug entry with success
      debugEntry.response = {
        success: true,
        data: { suggestion, meta },
        duration
      };
      debugEntry.parsedResult = suggestion;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI items suggestion generated in ${meta.duration}ms (request: ${meta.requestId})`);

      return suggestion;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Update debug entry with error
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);

      console.error('AI items generation failed:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else if (error.message.includes('Invalid authorization')) {
          throw new Error('Authentication failed. Please check your deployment configuration.');
        } else if (error.message.includes('not configured')) {
          throw new Error('AI provider not configured on server. Please check environment variables.');
        } else {
          throw new Error(`Failed to generate item suggestions: ${error.message}`);
        }
      } else {
        throw new Error('Failed to generate item suggestions. Please try again.');
      }
    }
  }

  private buildItemUserPrompt(request: AIItemRequest): string {
    const effectiveUserInput = request.userInput.trim() || 
      (request.existingItems.length > 0 
        ? 'Analyze the existing items and suggest complementary ones that would help achieve this goal'
        : 'Suggest actionable items to help achieve this goal and get started with this list');
    
    let prompt = `User Request: "${effectiveUserInput}"\n\n`;
    
    prompt += `List Context:\n`;
    prompt += `- Title: ${request.linkedList.title}\n`;
    prompt += `- Description: ${request.linkedList.description}\n\n`;
    
    prompt += `Linked Goal (SMART Framework):\n`;
    prompt += `- Title: ${request.linkedGoal.title}\n`;
    prompt += `- Description: ${request.linkedGoal.description}\n`;
    prompt += `- Specific: ${request.linkedGoal.smartCriteria.specific}\n`;
    prompt += `- Measurable: ${request.linkedGoal.smartCriteria.measurable}\n`;
    prompt += `- Achievable: ${request.linkedGoal.smartCriteria.achievable}\n`;
    prompt += `- Relevant: ${request.linkedGoal.smartCriteria.relevant}\n`;
    prompt += `- Time-bound: ${request.linkedGoal.smartCriteria.timeBound}\n\n`;
    
    if (request.existingItems.length > 0) {
      prompt += `Existing Items in List (avoid duplicates):\n`;
      prompt += request.existingItems.map((item, index) => 
        `${index + 1}. ${item.content}${item.completed ? ' ✓' : ''}${item.description ? ` - ${item.description}` : ''}`
      ).join('\n');
      prompt += '\n\n';
    }
    
    if (request.userProfile) {
      prompt += `User Context:\n`;
      if (request.userProfile.name) prompt += `Name: ${request.userProfile.name}\n`;
      if (request.userProfile.jobRole) prompt += `Role: ${request.userProfile.jobRole}\n`;
      if (request.userProfile.age) prompt += `Age: ${request.userProfile.age}\n`;
      prompt += '\n';
    }
    
    return prompt;
  }

  async generateScheduleSuggestion(request: AIScheduleRequest): Promise<AIScheduleSuggestion> {
    // Validation checks (same pattern as other methods)
    if (!environment.apiSecret) {
      throw new Error('API authentication not configured.');
    }
    
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled.');
    }

    const provider = 'anthropic';
    
    const systemPrompt = `You are an intelligent scheduling assistant helping users find optimal times for their tasks.
    Analyze the user's request, list details, existing schedule, and context to suggest the best scheduling times.
    
    Consider these factors:
    1. User's working hours and availability
    2. Existing scheduled items to avoid conflicts
    3. Task priority and deadline urgency
    4. Estimated duration and complexity
    5. Workload balance across days
    6. Buffer time between tasks
    7. SMART goal alignment and deadlines
    
    Suggest times that:
    - Respect working hours and avoid conflicts
    - Prioritize high-priority tasks in optimal time slots
    - Balance workload across available days
    - Provide adequate time for completion
    - Consider deadline requirements and urgency
    - Leave buffer time for transitions
    
    Return a JSON object with:
    - primaryTimeSlot: { date, time, duration }
    - alternativeTimeSlots: array of 2-3 alternatives with reasons
    - recurringPattern: (optional) if pattern detected
    - optimizationFactors: scores for various factors
    - reasoning: array of explanations
    - warnings: (optional) any scheduling concerns`;

    const userPrompt = this.buildScheduleUserPrompt(request);
    
    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: request.userInput,
        existingAspirations: 0,
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      const requestId = crypto.randomUUID();
      
      console.log(`📅 Sending AI Schedule request ${requestId} to backend...`);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (environment.isDevelopment && environment.apiSecret) {
        headers['Authorization'] = `Bearer ${environment.apiSecret}`;
      }
      
      const response = await fetch(`${environment.aiEndpoint}/schedule`, {
        method: 'POST',
        headers,
        credentials: environment.isProduction ? 'same-origin' : 'omit',
        body: JSON.stringify({
          ...request,
          provider,
          model: this.config?.model,
          requestId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const { data: suggestion, meta } = await response.json();
      const duration = Date.now() - startTime;

      debugEntry.response = {
        success: true,
        data: { suggestion, meta },
        duration
      };
      debugEntry.parsedResult = suggestion;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI schedule suggestion generated in ${meta.duration}ms`);

      return suggestion;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);
      console.error('AI schedule generation failed:', error);
      
      throw new Error(
        error instanceof Error 
          ? `Failed to generate schedule suggestion: ${error.message}`
          : 'Failed to generate schedule suggestion'
      );
    }
  }

  async optimizeWeeklySchedule(request: AIScheduleOptimizationRequest): Promise<AIScheduleOptimizationResult> {
    // Check environment configuration
    if (!environment.apiSecret) {
      throw new Error('API authentication not configured.');
    }
    
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled.');
    }

    const provider = 'anthropic';
    
    const systemPrompt = `You are a weekly schedule optimization expert.
    Analyze the user's current weekly schedule and suggest optimizations to improve:
    1. Workload balance across days
    2. Priority alignment (high-priority tasks get optimal time slots)
    3. Deadline compliance
    4. Buffer time between tasks
    
    Consider locked schedules that cannot be moved.
    Suggest moving schedules to better times and adding new schedules for unscheduled lists.
    
    Return optimization suggestions with improvement scores and reasoning.`;
    
    const userPrompt = this.buildScheduleOptimizationUserPrompt(request);

    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_schedule_opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: 'Weekly schedule optimization',
        existingAspirations: 0,
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      const requestId = crypto.randomUUID();
      
      console.log(`📊 Sending AI Schedule Optimization request ${requestId} to backend...`);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (environment.isDevelopment && environment.apiSecret) {
        headers['Authorization'] = `Bearer ${environment.apiSecret}`;
      }
      
      const response = await fetch(`${environment.aiEndpoint}/schedule-optimize`, {
        method: 'POST',
        headers,
        credentials: environment.isProduction ? 'same-origin' : 'omit',
        body: JSON.stringify({
          ...request,
          provider,
          model: this.config?.model,
          requestId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const { data: result, meta } = await response.json();
      const duration = Date.now() - startTime;

      debugEntry.response = {
        success: true,
        data: { result, meta },
        duration
      };
      debugEntry.parsedResult = result;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI schedule optimization completed in ${meta.duration}ms`);

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);
      console.error('AI schedule optimization failed:', error);
      
      throw new Error(
        error instanceof Error 
          ? `Failed to optimize schedule: ${error.message}`
          : 'Failed to optimize schedule'
      );
    }
  }

  private buildScheduleUserPrompt(request: AIScheduleRequest): string {
    let prompt = `Schedule Request: "${request.userInput || 'Find optimal time for this list'}"\n\n`;
    
    prompt += `List to Schedule:\n`;
    prompt += `- Title: ${request.listToSchedule.title}\n`;
    prompt += `- Description: ${request.listToSchedule.description}\n`;
    prompt += `- Priority: ${request.listToSchedule.priority}/10\n`;
    if (request.listToSchedule.estimatedHours) {
      prompt += `- Estimated Duration: ${request.listToSchedule.estimatedHours} hours\n`;
    }
    if (request.listToSchedule.deadline) {
      prompt += `- Deadline: ${request.listToSchedule.deadline}\n`;
    }
    prompt += `- Items: ${request.listToSchedule.items.length} tasks\n\n`;
    
    if (request.linkedGoal) {
      prompt += `Linked Goal (SMART):\n`;
      prompt += `- ${request.linkedGoal.title}\n`;
      prompt += `- Time-bound: ${request.linkedGoal.smartCriteria.timeBound}\n\n`;
    }
    
    prompt += `Working Hours:\n`;
    Object.entries(request.userContext.workingHours).forEach(([day, hours]) => {
      if (hours.enabled) {
        prompt += `- ${day}: ${hours.start} - ${hours.end}\n`;
      }
    });
    prompt += '\n';
    
    if (request.existingSchedules.length > 0) {
      prompt += `Existing Schedules (avoid conflicts):\n`;
      request.existingSchedules.forEach(schedule => {
        prompt += `- ${schedule.scheduledDate} ${schedule.scheduledTime}: ${schedule.listTitle}\n`;
      });
      prompt += '\n';
    }
    
    if (request.constraints) {
      prompt += `Constraints:\n`;
      if (request.constraints.earliestDate) {
        prompt += `- Earliest: ${request.constraints.earliestDate}\n`;
      }
      if (request.constraints.latestDate) {
        prompt += `- Latest: ${request.constraints.latestDate}\n`;
      }
      if (request.constraints.maxDailyHours) {
        prompt += `- Max daily hours: ${request.constraints.maxDailyHours}\n`;
      }
      prompt += '\n';
    }
    
    prompt += `Current Date/Time: ${request.userContext.currentDate}\n`;
    prompt += `Time Zone: ${request.userContext.timeZone}\n`;
    
    return prompt;
  }

  private buildScheduleOptimizationUserPrompt(request: AIScheduleOptimizationRequest): string {
    let prompt = `Weekly Schedule Optimization Request\n\n`;
    
    prompt += `Week Starting: ${request.weekStartDate}\n\n`;
    
    prompt += `Current Schedules:\n`;
    request.schedules.forEach(schedule => {
      prompt += `- ${schedule.currentDate} ${schedule.currentTime}: ${schedule.listTitle} (Priority: ${schedule.priority}, Duration: ${schedule.duration}min)`;
      if (schedule.isLocked) {
        prompt += ` [LOCKED - DO NOT MOVE]`;
      }
      prompt += `\n`;
    });
    prompt += '\n';
    
    prompt += `Unscheduled Lists:\n`;
    const scheduledListIds = request.schedules.map(s => s.listId);
    const unscheduledLists = request.lists.filter(l => !scheduledListIds.includes(l.id));
    unscheduledLists.forEach(list => {
      prompt += `- ${list.title} (Priority: ${list.priority}`;
      if (list.estimatedHours) {
        prompt += `, Est: ${list.estimatedHours}h`;
      }
      if (list.deadline) {
        prompt += `, Deadline: ${list.deadline}`;
      }
      prompt += `)\n`;
    });
    prompt += '\n';
    
    prompt += `Optimization Goals: ${request.optimizationGoals.join(', ')}\n\n`;
    
    prompt += `Working Hours:\n`;
    Object.entries(request.userContext.workingHours).forEach(([day, hours]) => {
      prompt += `- ${day}: ${hours.start} - ${hours.end}\n`;
    });
    
    if (request.userContext.preferredWorkStyles) {
      prompt += `\nPreferred Work Styles: ${request.userContext.preferredWorkStyles.join(', ')}\n`;
    }
    
    return prompt;
  }

  async generateAspirationSuggestion(request: AIAspirationRequest): Promise<AIAspirationSuggestion> {
    // Check if user has provided their API key
    if (!this.hasUserApiKey()) {
      throw new Error('API key required. Please add your Anthropic API key in Settings.');
    }

    // Check UI configuration
    if (!this.config?.enabled) {
      throw new Error('AI features are disabled. Please enable them in Settings.');
    }

    // Only use Anthropic now
    const provider = 'anthropic';

    const systemPrompt = `You are a life coach AI helping users create meaningful aspirations. 
    Based on the user's input, existing aspirations, and any current form data, suggest an aspiration that:
    1. Is specific, inspiring, and achievable
    2. Complements but doesn't duplicate existing aspirations
    3. Fits naturally into one of these categories: Career, Financial, Health, Education, Personal, Family, Travel, Social, Spiritual
    4. If current form data exists, enhance/refine it rather than completely replacing it
    5. Build upon partial information already provided by the user
    
    Return a JSON object with: title (max 100 chars), description (max 500 chars), category, and optionally suggestedGoals array and reasoning.`;

    const userPrompt = this.buildUserPrompt(request);

    // Create debug entry
    const debugEntry: AIDebugEntry = {
      id: `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      request: {
        userInput: request.userInput,
        existingAspirations: request.existingAspirations.length,
        provider,
        model: this.config?.model,
        systemPrompt,
        userPrompt,
        currentFormData: request.currentFormData
      },
      response: {
        success: false,
        duration: 0
      }
    };

    const startTime = Date.now();

    try {
      // Generate request ID for tracking
      const requestId = crypto.randomUUID();
      
      console.log(`🤖 Sending AI request ${requestId} to backend...`);

      // Call backend with user API key
      const { data: suggestion, meta } = await this.makeAIRequest('aspiration', {
        userInput: request.userInput,
        existingAspirations: request.existingAspirations,
        userProfile: request.userProfile,
        currentFormData: request.currentFormData,
        provider,
        model: this.config?.model,
        requestId
      });
      const duration = Date.now() - startTime;

      // Update debug entry with success
      debugEntry.response = {
        success: true,
        data: { suggestion, meta },
        duration
      };
      debugEntry.parsedResult = suggestion;

      this.saveDebugEntry(debugEntry);
      console.log(`✅ AI suggestion generated in ${meta.duration}ms (request: ${meta.requestId})`);

      return suggestion;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Update debug entry with error
      debugEntry.response = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration
      };

      this.saveDebugEntry(debugEntry);

      console.error('AI generation failed:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else if (error.message.includes('Invalid authorization')) {
          throw new Error('Authentication failed. Please check your deployment configuration.');
        } else if (error.message.includes('not configured')) {
          throw new Error('AI provider not configured on server. Please check environment variables.');
        } else {
          throw new Error(`Failed to generate suggestion: ${error.message}`);
        }
      } else {
        throw new Error('Failed to generate aspiration suggestion. Please try again.');
      }
    }
  }

  async generateAspirationReport(request: AIReportRequest): Promise<AIAspirationReport> {
    if (!this.isConfigured()) {
      throw new Error('AI service not configured. Please add your API key in Settings.');
    }

    const debugId = `report_${Date.now()}`;
    const startTime = Date.now();

    try {
      console.log('📊 Generating aspiration report:', {
        aspiration: request.aspiration.title,
        goals: request.goals.length,
        lists: request.lists.length
      });

      const systemPrompt = this.buildReportSystemPrompt();
      const userPrompt = this.buildReportUserPrompt(request);

      // Save debug entry
      const debugEntry: AIDebugEntry = {
        id: debugId,
        timestamp: new Date(),
        request: {
          userInput: `Report for: ${request.aspiration.title}`,
          existingAspirations: 1,
          provider: 'anthropic',
          model: this.config?.model,
          systemPrompt,
          userPrompt,
        },
        response: {
          success: false,
          duration: 0
        }
      };

      const result = await this.makeAIRequest('report', {
        ...request,
        provider: 'anthropic',
        model: this.config?.model || 'claude-3-5-sonnet-20241022'
      });

      // Update debug entry with success
      debugEntry.response = {
        success: true,
        data: result,
        duration: Date.now() - startTime
      };
      debugEntry.parsedResult = result;
      this.saveDebugEntry(debugEntry);

      return result;
    } catch (error: any) {
      console.error('❌ Report generation failed:', error);

      // Save debug entry with error
      const debugEntry: AIDebugEntry = {
        id: debugId,
        timestamp: new Date(),
        request: {
          userInput: `Report for: ${request.aspiration.title}`,
          existingAspirations: 1,
          provider: 'anthropic',
          model: this.config?.model,
          systemPrompt: '',
          userPrompt: ''
        },
        response: {
          success: false,
          error: error.message,
          duration: Date.now() - startTime
        }
      };
      this.saveDebugEntry(debugEntry);

      throw error;
    }
  }

  private buildReportSystemPrompt(): string {
    return `You are an expert productivity coach and data analyst helping users optimize their progress toward life aspirations.

    Your role is to:
    1. Analyze the current state of an aspiration and all related goals, lists, and items
    2. Calculate realistic progress metrics and timelines
    3. Identify patterns, strengths, weaknesses, and blockers
    4. Provide actionable, specific recommendations for improvement
    5. Suggest new goals or lists if gaps are identified
    6. Prioritize recommendations by impact

    Be encouraging but honest. Focus on practical, implementable suggestions.
    Use data to support your analysis. Be specific with numbers and percentages.`;
  }

  private buildReportUserPrompt(request: AIReportRequest): string {
    const totalItems = request.lists.reduce((sum, list) => sum + list.items.length, 0);
    const completedItems = request.lists.reduce((sum, list) =>
      sum + list.items.filter(item => item.completed).length, 0
    );

    return `Analyze this aspiration and generate a comprehensive progress report:

    ASPIRATION: "${request.aspiration.title}"
    Description: ${request.aspiration.description}
    Category: ${request.aspiration.category}
    Created: ${request.aspiration.createdAt}

    GOALS (${request.goals.length}):
    ${request.goals.map(g => `- ${g.title}: ${g.progress}% complete`).join('\n')}

    LISTS (${request.lists.length}):
    ${request.lists.map(l => `- ${l.title}: ${l.items.filter(i => i.completed).length}/${l.items.length} items complete`).join('\n')}

    OVERALL METRICS:
    - Total Items: ${totalItems}
    - Completed Items: ${completedItems}
    - Completion Rate: ${totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0}%

    Generate a detailed report with specific recommendations to improve progress.`;
  }
}

export const aiService = new AIService();

// Expose debug utilities in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).aiDebug = {
    // Get last AI request/response
    getLast: () => {
      const history = aiService.getDebugHistory();
      return history[0] || null;
    },
    
    // Get all history
    getAll: () => aiService.getDebugHistory(),
    
    // Get formatted debug data for copying
    export: () => {
      const data = aiService.exportDebugData();
      console.log('📋 Copy this data for debugging:');
      console.log(data);
      return data;
    },
    
    // Clear debug history
    clear: () => aiService.clearDebugHistory(),
    
    // Get raw localStorage data
    getRaw: () => ({
      config: localStorage.getItem('godmode_ai_config'),
      debug: localStorage.getItem('godmode_ai_debug')
    }),
    
    // Copy last request to clipboard (if available)
    copyLast: async () => {
      const last = aiService.getDebugHistory()[0];
      if (last && navigator.clipboard) {
        const text = JSON.stringify(last, null, 2);
        await navigator.clipboard.writeText(text);
        console.log('✅ Last AI interaction copied to clipboard');
        return true;
      }
      console.log('❌ No AI history to copy');
      return false;
    },

    // Environment debugging
    environment: () => {
      console.log('🌍 Environment Status:', environment.getConfigurationStatus());
      return environment.getConfigurationStatus();
    }
  };
  
  // console.log('🔧 AI Debug utilities available:');
  // console.log('  aiDebug.getLast()      - Get last AI interaction');
  // console.log('  aiDebug.getAll()       - Get all AI history');
  // console.log('  aiDebug.export()       - Export formatted debug data');
  // console.log('  aiDebug.clear()        - Clear debug history');
  // console.log('  aiDebug.getRaw()       - Get raw localStorage data');
  // console.log('  aiDebug.copyLast()     - Copy last interaction to clipboard');
  // console.log('  aiDebug.environment()  - Check environment configuration');
}